/**
 * Account Helper Functions
 * 
 * Utility functions for account-related calculations and operations.
 */

import { Currency, Account, AccountGroupDetailField } from "../types";

/**
 * Get currency rate (rate relative to default currency)
 * @param currencyCode - Currency code to get rate for
 * @param defaultCurrency - Default currency code
 * @param currencies - Array of currencies
 * @returns Currency rate (1 if same as default, or currency rate)
 */
export function getCurrencyRate(
  currencyCode: string,
  defaultCurrency: string,
  currencies: Currency[]
): number {
  if (currencyCode === defaultCurrency) return 1;
  const currency = currencies.find((c) => c.code === currencyCode);
  return currency?.rate || 1;
}

/**
 * Calculate transfer rate between two currencies
 * Rate structure: rate represents "1 defaultCurrency = rate currency"
 * Example: If default is USD, AMD rate = 380 means 1 USD = 380 AMD
 * Example: If default is USD, EUR rate = 0.85 means 1 USD = 0.85 EUR
 * 
 * @param fromCurrency - Source currency code
 * @param toCurrency - Destination currency code
 * @param defaultCurrency - Default currency code
 * @param currencies - Array of currencies
 * @returns Transfer rate to convert from fromCurrency to toCurrency
 */
export function calculateTransferRate(
  fromCurrency: string,
  toCurrency: string,
  defaultCurrency: string,
  currencies: Currency[]
): number {
  if (fromCurrency === toCurrency) return 1;
  const fromRate = getCurrencyRate(fromCurrency, defaultCurrency, currencies); // 1 defaultCurrency = fromRate fromCurrency
  const toRate = getCurrencyRate(toCurrency, defaultCurrency, currencies); // 1 defaultCurrency = toRate toCurrency
  
  // To convert fromCurrency to toCurrency:
  // 1. Convert to default: amount / fromRate (e.g., 380 AMD / 380 = 1 USD)
  // 2. Convert from default to toCurrency: (amount / fromRate) * toRate
  // So: transferRate = toRate / fromRate
  // Example: 1 AMD to EUR: (1 / 380) * 0.85 = 0.85 / 380 = 0.00224 (1 AMD = 0.00224 EUR)
  return toRate / fromRate; // Rate to convert from fromCurrency to toCurrency
}

/**
 * Format rate without trailing zeros, max 5 decimal places
 * @param rate - Rate number or string
 * @returns Formatted rate string
 */
export function formatRate(rate: number | string): string {
  const numRate = typeof rate === "string" ? parseFloat(rate) : rate;
  if (isNaN(numRate)) return "0";
  // Round to 5 decimal places and remove trailing zeros
  const rounded = Math.round(numRate * 100000) / 100000;
  // If it's a whole number, return without decimals
  if (rounded % 1 === 0) {
    return rounded.toString();
  }
  // Format with up to 5 decimal places and remove trailing zeros
  return rounded.toFixed(5).replace(/\.?0+$/, "");
}

/**
 * Calculate balance for a group of accounts (converted to default currency)
 * @param accounts - Array of accounts in the group
 * @param defaultCurrency - Default currency code
 * @param currencies - Array of currencies
 * @returns Total balance in default currency
 */
export function calculateGroupBalance(
  accounts: Account[],
  defaultCurrency: string,
  currencies: Currency[]
): number {
  let balance = 0;

  accounts.forEach((acc) => {
    const accountAmount = parseFloat(String(acc.amount || 0));
    
    // Convert account amount to default currency
    let amountInDefaultCurrency = accountAmount;
    
    if (acc.currency !== defaultCurrency) {
      const accountCurrencyRate = getCurrencyRate(acc.currency, defaultCurrency, currencies);
      amountInDefaultCurrency = accountAmount / accountCurrencyRate;
    }
    
    balance += amountInDefaultCurrency;
  });

  return balance;
}

/**
 * Get account details for display
 * @param account - Account to get details for
 * @param accountGroups - Array of account groups
 * @returns Array of detail objects with name and value
 */
export function getAccountDetailsForDisplay(
  account: Account,
  accountGroups: Array<{ name: string; details?: AccountGroupDetailField[] }>
): Array<{ name: string; value: string | number }> {
  // If account has details field (even if empty), show all detail fields from group
  if (account.details === undefined) return [];
  
  const group = accountGroups.find((g) => g.name === account.group);
  if (!group?.details) return [];
  
  // Return all detail fields from group, showing "-" for empty values
  return group.details.map((detail) => {
    const value = account.details?.[detail.name];
    return {
      name: detail.name,
      value: value !== undefined && value !== "" ? value : "-",
    };
  });
}

/**
 * Calculate converted amount for transfer display
 * @param amount - Transfer amount
 * @param transferRate - Transfer rate
 * @returns Converted amount or null if invalid
 */
export function getConvertedAmount(amount: string, transferRate: string): number | null {
  const parsedAmount = parseFloat(amount);
  if (isNaN(parsedAmount) || parsedAmount <= 0) return null;

  if (!transferRate || transferRate.trim() === "") {
    return null;
  }

  const rate = parseFloat(transferRate);
  if (isNaN(rate) || rate <= 0) return null;

  return parsedAmount * rate;
}
